import styled, { DefaultTheme } from "styled-components";
import { Card, Row, Modal, Button, Space, InputNumber, DatePicker, Form, Tag, Input, Select, Descriptions } from "antd";
import { TagOutlined } from "@ant-design/icons";

// Declare module augmentation for styled-components theme
declare module "styled-components" {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}
  
// Theme helper function
const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === "dark" ? darkValue : lightValue;
};

// Styled Components
export const Container = styled.div`
  padding: 16px 16px 16px 0px;
  background: ${(props) => getThemeValue(props, "#171923", "transparent")};
  color: ${(props) => getThemeValue(props, "#fff", "inherit")};
`;

export const StyledCard = styled(Card)`
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, "#718096", "#DCDCDC")};
  margin-bottom: 24px;
  background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};

  .ant-card-body {
    padding: 12px 12px 12px 12px;
    background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
  }
`;

export const StatsRow = styled(Row)`
  margin-bottom: 24px;
  
  @media (min-width: 992px) {
    .ant-col {
      flex: 1 1 0% !important;
      max-width: none !important;
    }
  }
`;

export const StatCard = styled(Card)<{ cardType?: string }>`
  --title-color: ${(props) => {
    const type = props.cardType || 'default';
    const colors: Record<string, string> = {
      total: '#722ed1',
      active: '#389e0d',
      draft: '#d46b08',
      paused: '#d48806',
      expired: '#595959',
      cancelled: '#cf1322',
      default: '#8c8c8c',
    };
    return colors[type] || colors.default;
  }};
  
  border-radius: 12px;
  width: 100%;
  height: 100%;
  border: 1px solid ${(props) => {
    const isDark = props.theme?.mode === "dark";
    const type = props.cardType || 'default';
    if (isDark) {
      const colors: Record<string, string> = {
        total: '#2b1d3f',
        active: '#162312',
        draft: '#2b1d0f',
        paused: '#2b2410',
        expired: '#262626',
        cancelled: '#2b1d0f',
        default: '#262626',
      };
      return colors[type] || colors.default;
    }
    const colors: Record<string, string> = {
      total: '#f9f0ff',
      active: '#f6ffed',
      draft: '#fff7e6',
      paused: '#fffbe6',
      expired: '#f5f5f5',
      cancelled: '#fff1f0',
      default: '#f0f0f0',
    };
    return colors[type] || colors.default;
  }};
  box-shadow: ${(props) => getThemeValue(props, "0 2px 8px rgba(0, 0, 0, 0.3)", "0 2px 8px rgba(0, 0, 0, 0.08)")};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
  background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${(props) => {
      const type = props.cardType || 'default';
      const colors: Record<string, string> = {
        total: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)',
        active: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
        draft: 'linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)',
        paused: 'linear-gradient(135deg, #faad14 0%, #d48806 100%)',
        expired: 'linear-gradient(135deg, #8c8c8c 0%, #595959 100%)',
        cancelled: 'linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)',
        default: 'linear-gradient(135deg, #8c8c8c 0%, #595959 100%)',
      };
      return colors[type] || colors.default;
    }};
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: ${(props) => getThemeValue(props, "0 8px 24px rgba(0, 0, 0, 0.5)", "0 8px 24px rgba(0, 0, 0, 0.12)")};
    border-color: ${(props) => {
      const isDark = props.theme?.mode === "dark";
      const type = props.cardType || 'default';
      if (isDark) {
        const colors: Record<string, string> = {
          total: '#4a2e6f',
          active: '#274916',
          draft: '#4a2e0f',
          paused: '#4a3e10',
          expired: '#434343',
          cancelled: '#4a2e0f',
          default: '#434343',
        };
        return colors[type] || colors.default;
      }
      const colors: Record<string, string> = {
        total: '#efdbff',
        active: '#d9f7be',
        draft: '#ffe7ba',
        paused: '#fff1b8',
        expired: '#d9d9d9',
        cancelled: '#ffccc7',
        default: '#d9d9d9',
      };
      return colors[type] || colors.default;
    }};
  }

  .ant-card-body {
    padding: 16px 18px;
  }

  .ant-statistic {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .ant-statistic-title {
    font-size: 13px;
    color: var(--title-color) !important;
    font-weight: 600;
    margin-bottom: 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ant-statistic-content {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    flex-wrap: wrap;
  }

  .ant-statistic-content-value {
    font-size: 32px;
    font-weight: 700;
    line-height: 1.2;
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${(props) => getThemeValue(props, "#fff", "inherit")};
  }

  .ant-statistic-content-prefix {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: ${(props) => {
      const type = props.cardType || 'default';
      const colors: Record<string, string> = {
        total: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
        active: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
        draft: 'linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)',
        paused: 'linear-gradient(135deg, #fffbe6 0%, #fff1b8 100%)',
        expired: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
        cancelled: 'linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)',
        default: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
      };
      return colors[type] || colors.default;
    }};
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    position: relative;
    
    .anticon {
      font-size: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      line-height: 1;
      margin: 0;
      padding: 0;
    }
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
  color: ${(props) => getThemeValue(props, "#fff", "inherit")};
`;

export const TopControlBar = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 16px;
  background: ${(props) => getThemeValue(props, "#1a202c", "#fff")};
  border-bottom: 1px solid ${(props) => getThemeValue(props, "#718096", "#f0f0f0")};
  gap: 16px;
  flex-wrap: wrap;
`;

export const StyledModal = styled(Modal)`
  z-index: 2000 !important;

  .ant-modal-mask {
    z-index: 2000 !important;
    background-color: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(4px);
  }

  .ant-modal-wrap {
    z-index: 2000 !important;
  }

  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: ${(props) => getThemeValue(props, "0 20px 60px rgba(0, 0, 0, 0.5)", "0 20px 60px rgba(0, 0, 0, 0.15)")};
    background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
  }

  .ant-modal-header {
    border-bottom: 1px solid ${(props) => getThemeValue(props, "#434343", "#f0f0f0")};
    padding: 20px 24px;
    background: ${(props) => getThemeValue(props, "#1a202c", "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)")};
    border-radius: 16px 16px 0 0;

    .ant-modal-title {
      font-size: 20px;
      font-weight: 600;
      color: ${(props) => getThemeValue(props, "#fff", "#1a1a1a")};
    }
  }

  .ant-modal-body {
    max-height: 70vh;
    overflow-y: auto;
    padding: 24px;
    background: ${(props) => getThemeValue(props, "#1a202c", "#fafafa")};
  }

  .ant-modal-footer {
    border-top: 1px solid ${(props) => getThemeValue(props, "#434343", "#f0f0f0")};
    background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
    padding-top: 10px;
    border-radius: 0 0 16px 16px;
  }

  .ant-modal-close {
    top: 20px;
    right: 24px;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    transition: all 0.2s;
    color: ${(props) => getThemeValue(props, "#fff", "inherit")};

    &:hover {
      background-color: ${(props) => getThemeValue(props, "#4a5568", "#f5f5f5")};
    }
  }
`;

export const CreateButton = styled(Button)`
  background-color: #2D6CCA !important;
  border-color: #2D6CCA !important;
  color: #fff !important;

  &:hover {
    background-color: #1e4d8f !important;
    border-color: #1e4d8f !important;
    color: #fff !important;
  }

  &:focus {
    background-color: #2D6CCA !important;
    border-color: #2D6CCA !important;
    color: #fff !important;
  }
`;

export const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

export const CloseButton = styled(Button)`
  border: 0 !important;
  color: #222222 !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  height: 38px !important;
  font-size: 16px !important;
  padding: 8px 16px !important;
  background-color: #EDEDED !important;

  &:hover {
    color: #222222 !important;
    background-color: #DEDEDE !important;
  }
`;

export const SubmitButton = styled(Button)`
  background-color: #2D6CCA;
  font-weight: 500 !important;
  border-radius: 8px !important;
  font-size: 16px !important;
  height: 38px !important;

  &:hover {
    background-color: #1e4d8f !important;
  }
`;

export const UpdateButton = styled(Button)`
  font-weight: 500 !important;
  background-color: #2D6CCA !important;
  border-color: #2D6CCA !important;
  color: #fff !important;

  &:hover {
    background-color: #1e4d8f !important;
    border-color: #1e4d8f !important;
    color: #fff !important;
  }

  &:focus {
    background-color: #2D6CCA !important;
    border-color: #2D6CCA !important;
    color: #fff !important;
  }
`;

export const InfoSection = styled.div`
  margin-bottom: 24px;
`;

export const InfoLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #8c8c8c;
  margin-bottom: 8px;
`;

export const InfoValue = styled.div`
  font-size: 14px;
  color: #262626;
`;

export const ModalFooterWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

export const FullWidthSpace = styled(Space.Compact)`
  width: 100% !important;
  
  /* Override Ant Design compact item border-radius removal with maximum specificity */
  & > .ant-picker-compact-item.ant-picker-compact-first-item .ant-picker,
  & > .ant-picker-compact-item.ant-picker-compact-first-item.ant-picker-sm .ant-picker,
  & > .ant-picker-compact-item.ant-picker-compact-first-item.ant-picker-lg .ant-picker {
    border-start-end-radius: 8px !important;
    border-end-end-radius: 8px !important;
    border-start-start-radius: 8px !important;
    border-end-start-radius: 8px !important;
    border-radius: 8px !important;
  }
  
  & > .ant-picker-compact-item.ant-picker-compact-last-item .ant-picker,
  & > .ant-picker-compact-item.ant-picker-compact-last-item.ant-picker-sm .ant-picker,
  & > .ant-picker-compact-item.ant-picker-compact-last-item.ant-picker-lg .ant-picker {
    border-start-start-radius: 8px !important;
    border-end-start-radius: 8px !important;
    border-start-end-radius: 8px !important;
    border-end-end-radius: 8px !important;
    border-radius: 8px !important;
  }
  
  & > .ant-picker-compact-item .ant-picker {
    border-radius: 8px !important;
  }
  
  /* Target all date pickers inside compact space */
  & > * .ant-picker {
    border-radius: 8px !important;
  }
`;

export const StyledInput = styled(Input)`
  border-radius: 8px !important;
  height: 38px !important;
`;

export const StyledTextArea = styled(Input.TextArea)`
  border-radius: 8px !important;
`;

export const StyledSelect = styled(Select)`
  .ant-select-selector {
    border-radius: 8px !important;
    height: 38px !important;
    
    .ant-select-selection-item {
      line-height: 36px !important;
    }
    
    .ant-select-selection-placeholder {
      line-height: 36px !important;
    }
  }
`;

export const FullWidthInputNumber = styled(InputNumber)`
  width: 100% !important;
  
  .ant-input-number {
    border-radius: 8px !important;
    height: 38px !important;
  }
  
  .ant-input-number-input {
    height: 36px !important;
  }
`;

export const FullWidthDatePicker = styled(DatePicker)`
  width: 100% !important;
  
  .ant-picker {
    border-radius: 8px !important;
    height: 38px !important;
    padding: 4px 11px !important;
  }
  
  .ant-picker-input > input {
    height: 30px !important;
  }
`;

export const GenerateButton = styled(Button)`
  border-radius: 8px !important;
  height: 38px !important;
  font-weight: 500 !important;
  border-color: #d9d9d9 !important;
  width: auto !important;
  min-width: 120px !important;
  
  &:hover {
    border-color: #2D6CCA !important;
    color: #2D6CCA !important;
  }
`;

export const DateFormItemWrapper = styled(Form.Item)`
  width: 50%;
  
  &:first-child {
    margin-right: 8px;
  }
  
  /* Force border-radius on date pickers inside this wrapper */
  .ant-picker {
    border-radius: 8px !important;
    border-start-end-radius: 8px !important;
    border-end-end-radius: 8px !important;
    border-start-start-radius: 8px !important;
    border-end-start-radius: 8px !important;
  }
  
  /* Override compact item styles */
  .ant-picker-compact-item .ant-picker {
    border-radius: 8px !important;
    border-start-end-radius: 8px !important;
    border-end-end-radius: 8px !important;
    border-start-start-radius: 8px !important;
    border-end-start-radius: 8px !important;
  }
`;

export const HelperText = styled.div`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, "#8c8c8c", "#8c8c8c")};
  margin-top: 4px;
`;

export const TitleIcon = styled(TagOutlined)`
  color: #722ed1;
`;

export const TitleText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${(props) => getThemeValue(props, "#fff", "inherit")};
`;

export const PromoCodeTag = styled(Tag)`
  font-family: monospace;
  font-size: 14px;
`;

export const DiscountValueText = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => getThemeValue(props, "#fff", "inherit")};
`;

export const DetailsCard = styled(Card)`
  background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
  border: 1px solid ${(props) => getThemeValue(props, "#434343", "#f0f0f0")};
  border-radius: 12px;
  box-shadow: ${(props) => getThemeValue(props, "0 2px 8px rgba(0, 0, 0, 0.3)", "0 2px 8px rgba(0, 0, 0, 0.04)")};

  .ant-card-body {
    background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
  }
`;

export const StyledDescriptions = styled(Descriptions)`
  background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
  border-radius: 12px;

  .ant-descriptions-item-label {
    font-weight: 600;
    color: ${(props) => getThemeValue(props, "#bfbfbf", "#595959")};
    background: ${(props) => getThemeValue(props, "#1a202c", "#fafafa")} !important;
  }

  .ant-descriptions-item-content {
    color: ${(props) => getThemeValue(props, "#fff", "#1a1a1a")};
  }

  .ant-descriptions-view {
    background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
  }

  .ant-descriptions-row {
    border-color: ${(props) => getThemeValue(props, "#434343", "#f0f0f0")} !important;
  }
`;


export const ActionIconButton = styled.div<{ iconColor?: string }>`
  align-items: center;
  border: 1px solid ${(props) => getThemeValue(props, "#434343", "#eee")};
  border-radius: 10px;
  display: flex;
  height: 30px;
  justify-content: center;
  padding: 8px;
  width: 30px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  background: ${(props) => getThemeValue(props, "transparent", "inherit")};

  svg {
    transition: color 0.2s;
  }

  &:hover {
    background-color: ${(props) => {
      const iconColor = props.iconColor || "#2D6CCA";
      if (iconColor === "#f5222d") {
        return getThemeValue(props, "rgba(245, 34, 45, 0.2)", "rgba(245, 34, 45, 0.1)");
      }
      return getThemeValue(props, "rgba(45, 108, 202, 0.2)", "rgba(45, 108, 202, 0.1)");
    }} !important;

    svg {
      color: ${(props) => props.iconColor || "#2D6CCA"} !important;
    }
  }
`;

export const PromoCodeWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const PromoCodeText = styled.span`
  font-family: monospace;
  background-color: ${(props) => getThemeValue(props, "rgba(45, 108, 202, 0.2)", "#2D6CCA1A")};
  color: ${(props) => getThemeValue(props, "#2D6CCA", "#2D6CCA")};
  padding: 4px 8px;
  border-radius: 4px;
`;

export const CopyCodeButton = styled.button`
  background-color: transparent;
  color: #8c8c8c;
  border: none;
  border-radius: 6px;
  padding: 4px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(140, 140, 140, 0.1);
  }

  &:active {
    background-color: rgba(140, 140, 140, 0.2);
  }
`;

export const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TableTitleText = styled.div`
  font-weight: 500;
  color: ${(props) => getThemeValue(props, "#fff", "#121212")};
`;

export const TableText = styled.span`
  color: ${(props) => getThemeValue(props, "#fff", "#121212")};
`;

export const DiscountText = styled.span`
  color: ${(props) => getThemeValue(props, "#fff", "#121212")};
  font-weight: 500;
`;